import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fulfillmentLifecycleApi } from '~/api/fulfillmentLifecycle';

/**
 * Custom hook for fetching fulfillment lifecycle dashboard data.
 *
 * @param {number|null} facilityId - The facility ID to query
 * @param {string} operatingDate - The operating date in YYYY-MM-DD format
 * @param {Object} filters - Optional filters
 * @param {string[]} [filters.orderSources] - Filter by order source
 * @param {string[]} [filters.deliveryTypes] - Filter by delivery type
 * @returns {Object} - { routes, summary, loading, error, refetch }
 */
const useFulfillmentLifecycleData = (facilityId, operatingDate, filters = {}) => {
    const [routes, setRoutes] = useState([]);
    const [summary, setSummary] = useState({
        totalRoutes: 0,
        totalShipments: 0,
        critical: 0,
        atRisk: 0,
        onTrack: 0,
        facilityThroughput: 0,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortControllerRef = useRef(null);

    // Stabilize filter dependencies to prevent unnecessary re-renders
    const orderSourcesKey = useMemo(() => (filters.orderSources || []).join(','), [filters.orderSources]);
    const deliveryTypesKey = useMemo(() => (filters.deliveryTypes || []).join(','), [filters.deliveryTypes]);

    const fetchData = useCallback(async () => {
        if (!facilityId || !operatingDate) {
            return;
        }

        // Cancel previous request if still in flight
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new AbortController for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setLoading(true);
        setError(null);

        try {
            const response = await fulfillmentLifecycleApi.getStatus({
                facilityId,
                operatingDate,
                orderSources: filters.orderSources || [],
                deliveryTypes: filters.deliveryTypes || [],
                signal: abortController.signal,
            });

            // Check if request was aborted
            if (abortController.signal.aborted) {
                return;
            }

            if (response.status) {
                setRoutes(response.data?.routes || []);
                setSummary(
                    response.data?.summary || {
                        totalRoutes: 0,
                        totalShipments: 0,
                        critical: 0,
                        atRisk: 0,
                        onTrack: 0,
                        facilityThroughput: 0,
                    },
                );
            } else {
                setError(response.msg || 'Failed to fetch dashboard data');
            }
        } catch (err) {
            // Ignore aborted requests
            if (err.name === 'AbortError') {
                return;
            }
            setError(err.message || 'An unexpected error occurred');
        } finally {
            // Only update loading state if request wasn't aborted
            if (!abortController.signal.aborted) {
                setLoading(false);
            }
        }
    }, [facilityId, operatingDate, orderSourcesKey, deliveryTypesKey]);

    useEffect(() => {
        fetchData();

        // Cleanup: abort request on unmount or when dependencies change
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchData]);

    return {
        routes,
        summary,
        loading,
        error,
        refetch: fetchData,
    };
};

export { useFulfillmentLifecycleData };
